import { useEffect, useRef, useCallback } from 'react';
import { KeycloakService } from '../services/keycloakService';

interface UseInactivityTimeoutOptions {
  inactivityTimeout?: number;
  onInactivityLogout?: () => void;
}

export const useInactivityTimeout = ({
  inactivityTimeout = 15 * 60 * 1000, // 15 minutes default
  onInactivityLogout
}: UseInactivityTimeoutOptions = {}) => {
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Funkcja do automatycznego wylogowania z powodu bezczynności
  const handleInactivityLogout = useCallback(() => {
    console.warn('Automatyczne wylogowanie z powodu bezczynności użytkownika');
    if (KeycloakService.isLoggedIn()) {
      KeycloakService.logout();
    }
    onInactivityLogout?.();
  }, [onInactivityLogout]);

  // Funkcja do resetowania timera bezczynności
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    // Ustawiamy nowy timer bezczynności tylko jeśli użytkownik jest zalogowany
    if (KeycloakService.isLoggedIn()) {
      inactivityTimeoutRef.current = setTimeout(handleInactivityLogout, inactivityTimeout);
    }
  }, [handleInactivityLogout, inactivityTimeout]);

  // Funkcja do obsługi aktywności użytkownika (resetuje timer)
  const handleActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Funkcja do obsługi zmiany widoczności strony
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      // Resetujemy timer bezczynności przy powrocie do aplikacji
      resetInactivityTimer();
    }
    // Gdy aplikacja staje się niewidoczna, nie resetujemy timera bezczynności
    // Pozwalamy mu działać w tle
  }, [resetInactivityTimer]);

  useEffect(() => {
    if (KeycloakService.isLoggedIn()) {
      // Inicjalizujemy timer bezczynności dla zalogowanego użytkownika
      resetInactivityTimer();
    }

    // Dodajemy nasłuchiwacze zdarzeń do wykrywania aktywności użytkownika
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Cleanup event listeners
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Cleanup timeout
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, [resetInactivityTimer, handleActivity, handleVisibilityChange]);

  return {
    resetInactivityTimer,
    clearInactivityTimeout: () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
    }
  };
};